import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import RecycleBin from "./pages/RecycleBin";
import Kanban from "./pages/Kanban";
import Prepost from "./pages/Prepost";
import Plenti from "./pages/Plenti";
import PublicPlenti from "./pages/PublicPlenti";
import CalendarPage from "./pages/CalendarPage";
import Products from "./pages/Products";
import Quotes from "./pages/Quotes";
import QuoteBuilder from "./pages/QuoteBuilder";
import Payments from "./pages/Payments";
import Users from "./pages/Users";
import SuperAdmin from "./pages/SuperAdmin";
import Settings from "./pages/Settings";
import Reports from "./pages/Reports";
import Timesheets from "./pages/Timesheets";
import CallLog from "./pages/CallLog";
import Messages from "./pages/Messages";
import Templates from "./pages/Templates";
import PublicQuote from "./pages/PublicQuote";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataDeletion from "./pages/DataDeletion";
import Terms from "./pages/Terms";
import Booking from "./pages/Booking";
import ErrorLogs from "./pages/ErrorLogs";
import NotificationSettings from "./pages/NotificationSettings";
import ProductRecycleBin from "./pages/ProductRecycleBin";
import Chat from "./pages/Chat";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const Guard = ({ module, children }) => {
  const { hasModule } = useAuth();
  if (!hasModule(module)) return <Navigate to="/app" replace />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/quote/:token" element={<PublicQuote />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/data-deletion" element={<DataDeletion />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/book/:token" element={<Booking />} />
    <Route path="/plenti" element={<PublicPlenti />} />
    <Route path="/app" element={<Protected><Layout /></Protected>}>
      <Route index element={<Dashboard />} />
      <Route path="leads" element={<Guard module="leads"><Leads /></Guard>} />
      <Route path="leads/recycle-bin" element={<Guard module="leads"><RecycleBin /></Guard>} />
      <Route path="leads/:id" element={<Guard module="leads"><LeadDetail /></Guard>} />
      <Route path="kanban" element={<Guard module="kanban"><Kanban /></Guard>} />
      <Route path="quote-expired" element={<Guard module="kanban"><Kanban expiredOnly /></Guard>} />
      <Route path="chat" element={<Chat />} />
      <Route path="prepost" element={<Guard module="prepost"><Prepost /></Guard>} />
      <Route path="calendar" element={<Guard module="calendar"><CalendarPage /></Guard>} />
      <Route path="products" element={<Guard module="products"><Products /></Guard>} />
      <Route path="quotes" element={<Guard module="quotes"><Quotes /></Guard>} />
      <Route path="quotes/new" element={<Guard module="quotes"><QuoteBuilder /></Guard>} />
      <Route path="quotes/:id" element={<Guard module="quotes"><QuoteBuilder /></Guard>} />
      <Route path="plenti" element={<Guard module="plenti"><Plenti /></Guard>} />
      <Route path="messages" element={<Guard module="messages"><Messages /></Guard>} />
      <Route path="templates" element={<Guard module="templates"><Templates /></Guard>} />
      <Route path="payments" element={<Guard module="payments"><Payments /></Guard>} />
      <Route path="users" element={<Guard module="users"><Users /></Guard>} />
      <Route path="superadmin" element={<Guard module="superadmin"><SuperAdmin /></Guard>} />
      <Route path="settings" element={<Guard module="settings"><Settings /></Guard>} />
      <Route path="reports" element={<Guard module="reports"><Reports /></Guard>} />
      <Route path="timesheets" element={<Timesheets />} />
      <Route path="calllog" element={<Guard module="calllog"><CallLog /></Guard>} />
      <Route path="error-logs" element={<ErrorLogs />} />
      <Route path="notifications" element={<NotificationSettings />} />
      <Route path="products/recycle-bin" element={<Guard module="products"><ProductRecycleBin /></Guard>} />
    </Route>
    <Route path="*" element={<Navigate to="/app" replace />} />
  </Routes>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" richColors duration={2500} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
